import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { projectsApi, organisationsApi, organisationContactsApi, locationApi } from '../services/api';
import OrganisationContactSelector from '../components/OrganisationContactSelector';

const steps = ['Project Details', 'Review & Create'];

const getProjectDisplayNumber = (project) =>
  project.project_no ? `Project ${project.project_no}` : `Project #${project.project_id}`;

const getProjectLabel = (project) =>
  `${project.organisation_name} - ${getProjectDisplayNumber(project)}${project.quote_reference ? ` - ${project.quote_reference}` : ''}`;

const initialQuoteData = {
  // Organisation and contact info (new structure)
  selectedOrganisation: null,
  selectedContact: null,
  organisation_id: null,
  organisation_contact_id: null,
  // Legacy fields - kept for migration
  organisation_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postcode: '',
  what_three_words: '',
  duration_in_days: '',
  quote_reference: '',
  quote_type: 'Standard',
};

function NewQuote() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [quoteData, setQuoteData] = useState(initialQuoteData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isLocating, setIsLocating] = useState(false);

  // New state for project duplication
  const [isDuplicationMode, setIsDuplicationMode] = useState(false);
  const [selectedProjectToDuplicate, setSelectedProjectToDuplicate] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [duplicateDocuments, setDuplicateDocuments] = useState(true);

  // Load available projects for duplication
  useEffect(() => {
    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await projectsApi.getAll(null, true);
        // Allow duplication from projects of any status
        setAvailableProjects(response.data);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setProjectsLoading(false);
      }
    };

    loadProjects();
  }, []);

  const handleNext = () => {
    if (!validateStep(activeStep)) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleQuoteDataChange = (event) => {
    const { name, value } = event.target;
    setQuoteData({
      ...quoteData,
      [name]: value,
    });
  };

  const handleOrganisationChange = (organisation) => {
    setQuoteData({
      ...quoteData,
      selectedOrganisation: organisation,
      organisation_id: organisation?.organisationid || null,
      organisation_name: organisation?.name || '',
    });
  };

  const handleContactChange = (contact) => {
    setQuoteData({
      ...quoteData,
      selectedContact: contact,
      organisation_contact_id: contact?.organisationcontactid || null,
    });
  };

  // Handle project duplication mode toggle
  const handleDuplicationModeChange = (event) => {
    const enabled = event.target.checked;
    setIsDuplicationMode(enabled);
    
    if (enabled) {
      setDuplicateDocuments(true);
    } else {
      // Reset to fresh quote mode
      setSelectedProjectToDuplicate(null);
      setQuoteData(initialQuoteData);
      setDuplicateDocuments(false);
    }
  };

  // Handle project selection for duplication
  const handleProjectSelection = async (project) => {
    if (!project) {
      setSelectedProjectToDuplicate(null);
      setQuoteData(initialQuoteData);
      return;
    }

    setSelectedProjectToDuplicate(project);
    
    // For duplication mode, we need to fetch organization and contact details
    let selectedOrganisation = null;
    let selectedContact = null;
    
    if (project.organisation_id) {
      try {
        // Fetch organization details if we have an organisation_id
        const orgResponse = await organisationsApi.getById(project.organisation_id);
        selectedOrganisation = orgResponse.data;
      } catch (error) {
        console.warn('Could not fetch organization details:', error);
        // Fallback to creating a mock organization object
        selectedOrganisation = {
          name: project.organisation_name,
          organisationid: project.organisation_id
        };
      }
    } else {
      // Fallback for legacy projects without organisation_id
      selectedOrganisation = {
        name: project.organisation_name,
        organisationid: null
      };
    }

    if (project.organisation_contact_id) {
      try {
        // Fetch contact details if we have an organisation_contact_id
        const contactResponse = await organisationContactsApi.getById(project.organisation_contact_id);
        selectedContact = contactResponse.data;
      } catch (error) {
        console.warn('Could not fetch contact details:', error);
        selectedContact = null;
      }
    }
    
    // Pre-populate form with selected project data
    setQuoteData({
      ...initialQuoteData,
      organisation_name: project.organisation_name,
      address_line1: project.address_line1,
      address_line2: project.address_line2 || '',
      city: project.city,
      postcode: project.postcode,
      what_three_words: project.what_three_words || '',
      duration_in_days: project.duration_in_days || '',
      quote_type: project.quote_type || 'Standard',
      // Set the organization and contact objects
      selectedOrganisation: selectedOrganisation,
      selectedContact: selectedContact,
      organisation_id: project.organisation_id,
      organisation_contact_id: project.organisation_contact_id,
    });
  };

  const validateStep = (step) => {
    if (step === 0) {
      // All details validation on first step
      if (isDuplicationMode) {
        // In duplication mode, we need a selected project, organisation, and address fields
        return (
          selectedProjectToDuplicate &&
          quoteData.selectedOrganisation &&
          quoteData.address_line1 &&
          quoteData.city &&
          quoteData.postcode
        );
      } else {
        // Fresh quote needs organisation and address fields
        return (
          quoteData.selectedOrganisation &&
          quoteData.address_line1 &&
          quoteData.city &&
          quoteData.postcode
        );
      }
    }
    return true;
  };

  const handleCreateQuote = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isDuplicationMode && selectedProjectToDuplicate) {
        // Use the duplication API - include all edited fields
        const duplicatePayload = {
          organisation_name: quoteData.organisation_name,
          organisation_id: quoteData.selectedOrganisation?.organisationid,
          organisation_contact_id: quoteData.selectedContact?.organisationcontactid,
          address_line1: quoteData.address_line1,
          address_line2: quoteData.address_line2,
          city: quoteData.city,
          postcode: quoteData.postcode,
          what_three_words: quoteData.what_three_words,
          quote_reference: quoteData.quote_reference || '',
          quote_type: quoteData.quote_type,
          duplicate_documents: duplicateDocuments,
        };
        
        // Only include duration_in_days if it was provided and is valid
        if (quoteData.duration_in_days && !isNaN(parseInt(quoteData.duration_in_days, 10))) {
          duplicatePayload.duration_in_days = parseInt(quoteData.duration_in_days, 10);
        }

        const response = await projectsApi.duplicate(selectedProjectToDuplicate.project_id, duplicatePayload);
        
        setSnackbar({
          open: true,
          message: `Quote created successfully from existing project${duplicateDocuments ? ' (including documents)' : ''}!`,
          severity: 'success',
        });

        // Navigate to the new project's detail page
        navigate(`/projects/${response.data.project_id}`);
      } else {
        // Create fresh quote
        // Prepare quote payload - only include duration if provided
        const quotePayload = {
          ...quoteData,
          // Add organisation and contact IDs
          organisation_id: quoteData.selectedOrganisation?.organisationid,
          organisation_contact_id: quoteData.selectedContact?.organisationcontactid,
          // Remove the old organisation_name field and selected objects
        };
        
        // Remove the selected objects as they're not needed in the API
        delete quotePayload.selectedOrganisation;
        delete quotePayload.selectedContact;
        delete quotePayload.organisation_name; // Remove legacy field
        
        // Only include duration_in_days if it was provided and is valid
        if (quoteData.duration_in_days && !isNaN(parseInt(quoteData.duration_in_days, 10))) {
          quotePayload.duration_in_days = parseInt(quoteData.duration_in_days, 10);
        } else {
          // Remove duration_in_days if it's empty or invalid
          delete quotePayload.duration_in_days;
        }

        const response = await projectsApi.create(quotePayload);
        
        setSnackbar({
          open: true,
          message: 'Quote created successfully!',
          severity: 'success',
        });

        // Navigate to the new project's detail page
        navigate(`/projects/${response.data.project_id}`);
      }
    } catch (err) {
      console.error('Error creating quote:', err);
      setLoading(false);
      setError(err.response?.data?.error || 'Failed to create quote. Please try again.');
      
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to create quote. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  // Geolocation + What3Words + Google Geocoding integration
  const handleGetLocation = async () => {
    if (!('geolocation' in navigator)) {
      setSnackbar({ open: true, message: 'Geolocation is not supported by your browser.', severity: 'error' });
      return;
    }

    setIsLocating(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        });
      });

      const { latitude, longitude } = position.coords;

      // Call What3Words and Google Geocoding in parallel
      await Promise.all([
        fetchWhat3Words(latitude, longitude).catch((e) => {
          console.warn('What3Words lookup failed:', e);
          setSnackbar((s) => ({ ...s, open: true, message: 'Could not retrieve What3Words for your location.', severity: 'warning' }));
        }),
        reverseGeocodeWithGoogle(latitude, longitude).catch((e) => {
          console.warn('Google reverse geocoding failed:', e);
          setSnackbar((s) => ({ ...s, open: true, message: 'Could not reverse geocode address from your location.', severity: 'warning' }));
        }),
      ]);

      setSnackbar({ open: true, message: 'Location detected and fields updated.', severity: 'success' });
    } catch (err) {
      console.error('Geolocation error:', err);
      let message = 'Failed to get your current location.';
      if (err?.code === 1) message = 'Location permission denied. Please allow access and try again.'; // PERMISSION_DENIED
      if (err?.code === 2) message = 'Position unavailable. Please check your connection.';
      if (err?.code === 3) message = 'Location request timed out. Please try again.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsLocating(false);
    }
  };

  const fetchWhat3Words = async (lat, lng) => {
    try {
      const res = await locationApi.getWhat3Words(lat, lng);
      const data = res.data;
      if (data?.words) {
        setQuoteData((prev) => ({ ...prev, what_three_words: data.words }));
      } else if (data?.error) {
        throw new Error(data.error?.message || 'Unknown W3W error');
      }
    } catch (error) {
      console.warn('What3Words lookup failed:', error);
      throw error;
    }
  };

  const reverseGeocodeWithGoogle = async (lat, lng) => {
    try {
      const res = await locationApi.getGeocode(lat, lng);
      const data = res.data;

      // Backend now returns a normalized object: { address_line1, address_line2, city, postcode, formatted_address, raw }
      if (data.error) {
        throw new Error(data.error);
      }

      const { address_line1, city, postcode, formatted_address } = data;

      // Debug: leave a trace in console to help with any future issues
      console.debug('reverseGeocode result (from backend)', { address_line1, city, postcode, formatted_address });

      setQuoteData((prev) => ({
        ...prev,
        address_line1: address_line1 || prev.address_line1,
        address_line2: prev.address_line2,
        city: city || prev.city,
        postcode: postcode || prev.postcode,
      }));
    } catch (error) {
      console.warn('Google reverse geocoding failed:', error);
      throw error;
    }
  };


  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isDuplicationMode}
                    onChange={handleDuplicationModeChange}
                    color="primary"
                  />
                }
                label="Duplicate from existing project"
                sx={{ mb: 2 }}
              />
            </Grid>

            {isDuplicationMode && (
              <Grid item xs={12}>
                <Autocomplete
                  options={availableProjects}
                  getOptionLabel={(option) => `${getProjectLabel(option)} (${option.status})`}
                  value={selectedProjectToDuplicate}
                  onChange={(event, newValue) => handleProjectSelection(newValue)}
                  loading={projectsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Project to Duplicate"
                      required
                      helperText="Search by project number, organisation, or quote reference"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {projectsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.organisation_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getProjectDisplayNumber(option)}{option.quote_reference ? ` • ${option.quote_reference}` : ''} • {option.status} • {option.address_line1}, {option.city}
                        </Typography>
                      </Box>
                      <Chip 
                        label={option.status} 
                        size="small" 
                        color={
                          option.status === 'Quote' ? 'warning' :
                          option.status === 'Sales Order' ? 'success' :
                          option.status === 'Completed' ? 'primary' :
                          option.status === 'Cancelled' ? 'error' : 'default'
                        }
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  )}
                />
              </Grid>
            )}

            {selectedProjectToDuplicate && (
              <>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Duplicating from:</strong> {getProjectLabel(selectedProjectToDuplicate)}
                    </Typography>
                    <Typography variant="body2">
                      All project items will be copied. Choose whether to include documents below.
                    </Typography>
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={duplicateDocuments}
                        onChange={(e) => setDuplicateDocuments(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Duplicate documents (files)"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Include all documents/files from the original project. Forms and RAMs will not be copied.
                  </Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <OrganisationContactSelector
                selectedOrganisation={quoteData.selectedOrganisation}
                selectedContact={quoteData.selectedContact}
                onOrganisationChange={handleOrganisationChange}
                onContactChange={handleContactChange}
                required
                disabled={isDuplicationMode && !selectedProjectToDuplicate}
                disableContactUntilOrganisation={true}
              />
              {isDuplicationMode && selectedProjectToDuplicate && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Organisation and contact details are pre-filled from the selected project, but you can modify them if needed.
                </Typography>
              )}
              {isDuplicationMode && !selectedProjectToDuplicate && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Select a project above to pre-fill organisation details.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                Project Address
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={!isLocating && <MyLocationIcon />}
                onClick={handleGetLocation}
                disabled={isLocating}
                sx={{ mt: 2, mb: 1 }}
              >
                {isLocating ? 'Locating…' : 'Use my location'}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="Address Line 1"
                name="address_line1"
                value={quoteData.address_line1}
                onChange={handleQuoteDataChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                name="address_line2"
                value={quoteData.address_line2}
                onChange={handleQuoteDataChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="City"
                name="city"
                value={quoteData.city}
                onChange={handleQuoteDataChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label="Postcode"
                name="postcode"
                value={quoteData.postcode}
                onChange={handleQuoteDataChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="What3Words Location"
                name="what_three_words"
                value={quoteData.what_three_words}
                onChange={handleQuoteDataChange}
                helperText="Optional: Enter the What3Words address for precise location"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (in days)"
                name="duration_in_days"
                type="number"
                value={quoteData.duration_in_days}
                onChange={handleQuoteDataChange}
                InputProps={{ inputProps: { min: 1 } }}
                helperText="Optional: Duration can be set when converting to Sales Order"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quote Reference"
                name="quote_reference"
                value={quoteData.quote_reference}
                onChange={handleQuoteDataChange}
                helperText="Optional: Enter your internal reference number"
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>

            
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {isDuplicationMode ? 'Source Project' : 'Customer Details'}
                </Typography>
                {isDuplicationMode && selectedProjectToDuplicate ? (
                  <>
                    <Typography>
                      <strong>Original Project:</strong> {getProjectLabel(selectedProjectToDuplicate)}
                    </Typography>
                    <Typography>
                      <strong>Status:</strong> {selectedProjectToDuplicate.status}
                    </Typography>
                    <Typography>
                      <strong>Include Documents:</strong> {duplicateDocuments ? 'Yes - Documents will be copied' : 'No - Documents will not be copied'}
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                  </>
                ) : null}
                <Typography>
                  <strong>Organisation:</strong> {quoteData.organisation_name}
                </Typography>
                <Typography>
                  <strong>Address:</strong> {quoteData.address_line1}
                  {quoteData.address_line2 && `, ${quoteData.address_line2}`}, {quoteData.city}, {quoteData.postcode}
                </Typography>
                {quoteData.what_three_words && (
                  <Typography>
                    <strong>What3Words:</strong> {quoteData.what_three_words}
                  </Typography>
                )}
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Project Details
                </Typography>
                <Typography>
                  <strong>Duration:</strong> {quoteData.duration_in_days ? `${quoteData.duration_in_days} days` : 'Not specified (can be set when converting to Sales Order)'}
                </Typography>
                <Typography>
                  <strong>Quote Type:</strong> {quoteData.quote_type}
                </Typography>
                {quoteData.quote_reference && (
                  <Typography>
                    <strong>Reference:</strong> {quoteData.quote_reference}
                  </Typography>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                {isDuplicationMode 
                  ? `After creating this quote, all items from the original project will be copied${duplicateDocuments ? ', along with all documents/files' : ''}. You can then modify items, upload additional files, and manage activities as needed.`
                  : 'After creating this quote, you\'ll be able to add project items, upload files, and manage activities.'
                }
              </Typography>
            </Grid>
          </Grid>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, mt: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Create New Quote
        </Typography>
        <Box sx={{ mt: 4 }}>
          {renderStepContent(activeStep)}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {activeStep !== 0 && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateQuote}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {isDuplicationMode ? 'Duplicate as Quote' : 'Create Quote'}
              </Button>
            ) : (
              <Button variant="contained" color="primary" onClick={handleNext}>
                Review
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default NewQuote;
